import { Close as CloseIcon } from '@mui/icons-material'
import { useMediaQuery } from '@mui/material'
import { observer } from 'mobx-react-lite'
import { Fragment, ReactElement, useEffect, useMemo, useState } from 'react'
import { NonCachedSearchType } from 'interfaces'
import { useUiStateStore } from '../../../../../src/store/uiState/UiStateStore'
import { useUserStore } from '../../../../../src/store/user/UserStore'
import styled from 'styled-components'
import { useResultsStore } from '../../store/ResultsStore'
import { defaultTheme } from '../../../../../src/theme/index'
import { ListItem } from '../../../../../src/features/filterResults/interfaces'
import { SelectLinesDrawer } from './SelectLinesDrawer'
import { SelectWrapper } from '../../../../../src/features/filterResults/components/SelectWrapper'
import { StockFilterSection } from '../../../../../src/features/filterResults/components/StockFilterSection'
import {
  buildStockFilterOptions,
  getActiveStockOptionValue,
} from '../../../../../src/features/filterResults/buildStockFilterOptions'
import { SelectedLines } from './SelectedLines'
import {
  IstockOption,
  stockOptionsCases,
} from '../../../../../src/features/filterResults/components/stockOptionsCases'
import { useSearchStore } from 'src/store/search/SearchStore'
import { useSellerStore } from 'src/store/search/SellerStore'
import { useSearchFilterActions } from '../../../../../src/features/filterResults/useSearchFilterActions'
import { FilterGroupName, LineTypes } from '../../constants'
import { FilterAccordionSection } from '../../../../../src/features/filterResults/components/FilterAccordionSection'
import { CheckboxFilterGroup } from '../../../../../src/features/filterResults/components/CheckboxFilterGroup'
import {
  buildRenderableFilterSections,
  type FilterGroupModel,
} from '../../../../../src/features/filterResults/orderFilterSections'
import { getSectionAppliedFilterCount } from '../../../../../src/features/filterResults/getSectionAppliedFilterCount'
import { getFilterGroupTitle } from '../../../../../src/features/filterResults/getFilterGroupTitle'
import { getFilterSectionDefaultExpanded } from '../../../../../src/features/filterResults/getFilterSectionDefaultExpanded'
import { FilterSectionId } from '../../../../../src/features/filterResults/constants'

import { applyStagedFilterChange } from '../../../../../src/features/filterResults/applyStagedFilterChange'
import type { Filters } from 'src/store/models/SearchModels'

export interface FilterResultsMenuProps {
  handleOnClose?: () => void
  variant?: 'sidebar' | 'drawer'
  stagedFilters?: Filters
  onStagedFiltersChange?: (filters: Filters) => void
  stagedManufacturers?: string[]
  onStagedManufacturersChange?: (manufacturers: string[]) => void
}

export const FilterResultsMenu = observer(
  ({
    handleOnClose,
    variant = 'sidebar',
    stagedFilters,
    onStagedFiltersChange,
    stagedManufacturers,
    onStagedManufacturersChange,
  }: FilterResultsMenuProps): ReactElement => {
    const isDrawer = variant === 'drawer'
    const resultsStore = useResultsStore()
    const {
      lookupTitle,
      filters: selectedFilters,
      searchResults,
      currentAvailableFilters,
      firstSelectedFilterType,
      searchByGfxPartBin,
      searchByPartBin,
      selectedLinesManufacturers,
      setSelectedLinesManufacturers,
      clearResults,
      lastNonCachedSearch,
      showAllLinesForOrangeParts,
      partsBinId,
    } = resultsStore

    const { gfxLookupMade, forceClearGfx, currentVehicle } = useSearchStore()

    const { setShowFilterLinesDrawer } = useUiStateStore()
    const { getSelectedSeller } = useSellerStore()

    const userStore = useUserStore()

    const filtersCollapsed = useMediaQuery('(max-width:1460px)') && !isDrawer

    const displayFiltering = userStore?.preferences?.display_filtering

    const displayUserFiltering = userStore?.preferences?.display_userFiltering

    const [selectedFilterName, setSelectedFilterName] = useState<string | null>(
      null
    )

    const availableFilters = useMemo(
      () => searchResults?.filters || [],
      [searchResults?.filters]
    )

    const [activeBoolean, setActiveBoolean] = useState(true)

    const [stockOptions, setStockOptions] = useState<IstockOption>({
      groupName: FilterGroupName.STOCK,
      items: [],
    })

    const [showSelectionsList, setShowSelectionList] = useState<boolean>(false)

    const activeFilters =
      isDrawer && stagedFilters ? stagedFilters : selectedFilters
    const activeManufacturers =
      isDrawer && stagedManufacturers !== undefined
        ? stagedManufacturers
        : selectedLinesManufacturers

    useEffect(() => {
      if (isDrawer) {
        return
      }
      if (selectedLinesManufacturers.length > 0) {
        clearResults()
        if (gfxLookupMade) {
          searchByGfxPartBin()
        }
        searchByPartBin(undefined, selectedFilterName || undefined)
      }
    }, [
      isDrawer,
      selectedLinesManufacturers,
      gfxLookupMade,
      searchByPartBin,
      searchByGfxPartBin,
      clearResults,
      selectedFilterName,
    ])

    useEffect(() => {
      const linesFilter = (availableFilters || []).find(
        (filter) => filter.groupName === FilterGroupName.LINES
      )
      if (!linesFilter) {
        return
      }
      const isAllLinesSelected = linesFilter.items.some(
        (item) => item === LineTypes.ALL_LINES
      )
      if (isAllLinesSelected) {
        setShowSelectionList(true)
      }
    }, [availableFilters])

    const { applyFilterChange, resetLinesSelection } = useSearchFilterActions()

    const handleOnFiltersChange = (
      filterName: string,
      selectedOptions: ListItem[],
      clearExisting = false
    ): void => {
      if (isDrawer && onStagedFiltersChange && stagedFilters) {
        onStagedFiltersChange(
          applyStagedFilterChange(
            filterName,
            selectedOptions,
            stagedFilters,
            clearExisting
          )
        )
        return
      }
      setSelectedFilterName(filterName)
      applyFilterChange(filterName, selectedOptions, clearExisting)
    }

    const isItemActive = (filter: FilterGroupModel, item: string): boolean => {
      if (activeFilters.length === 0) {
        return false
      }
      const fil = activeFilters.find(
        (itemSel) => itemSel.groupName === filter.groupName
      )
      return fil ? fil.items.indexOf(item) > -1 : false
    }

    const handleResetSelection = (): void => {
      if (isDrawer && onStagedFiltersChange && stagedFilters) {
        onStagedFiltersChange(
          applyStagedFilterChange(FilterGroupName.LINES, [], stagedFilters)
        )
        onStagedManufacturersChange?.([])
        return
      }
      resetLinesSelection()
    }

    useEffect(() => {
      setStockOptions(stockOptionsCases(displayFiltering))
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
      if (activeFilters.length > 0) {
        setActiveBoolean(false)
      }
    }, [activeFilters])

    useEffect(() => {
      if (showAllLinesForOrangeParts) {
        setShowFilterLinesDrawer(true)
        setShowSelectionList(true)
      }
    }, [setShowFilterLinesDrawer, showAllLinesForOrangeParts])

    const isItemDisabled = (
      filter: FilterGroupModel,
      item: string
    ): boolean => {
      if (activeFilters.length === 0 || !firstSelectedFilterType) {
        return false
      }

      const isBrandOrPartType =
        filter.groupName === FilterGroupName.BRANDS ||
        filter.groupName === FilterGroupName.PART_TYPE

      if (!isBrandOrPartType || filter.groupName === firstSelectedFilterType) {
        return false
      }

      const fil = currentAvailableFilters.find(
        (itemSel) => itemSel.groupName === filter.groupName
      )

      return fil ? !fil.items.includes(item) : false
    }

    const stockFilterFromResults = availableFilters?.find(
      (filter) => filter.groupName === FilterGroupName.STOCK
    )

    const showStockFilter = Boolean(
      stockFilterFromResults &&
        stockFilterFromResults.items.length > 0 &&
        stockOptions.items.length > 0 &&
        activeManufacturers.length === 0
    )

    const stockFilterOptions =
      showStockFilter && stockFilterFromResults
        ? buildStockFilterOptions(
            stockOptions.items,
            (item) => isItemActive(stockFilterFromResults, item),
            displayUserFiltering,
            activeBoolean
          )
        : []

    const selectedStockValue =
      getActiveStockOptionValue(stockFilterOptions) ?? stockOptions.items[0]

    const handleStockFilterSelect = (option: ListItem): void => {
      if (activeBoolean) {
        setActiveBoolean(false)
      }
      handleOnFiltersChange(FilterGroupName.STOCK, [option])
    }

    const coverageKey = getSelectedSeller()?.coverageKey

    const renderableFilters = useMemo(() => {
      const shouldHideLinesFilter = (filter: FilterGroupModel): boolean => {
        if (filter.groupName !== FilterGroupName.LINES) {
          return false
        }
        if (coverageKey) {
          return true
        }
        if (
          !currentVehicle?.engine &&
          (lastNonCachedSearch ===
            NonCachedSearchType.searchByMultiPartsInquiry ||
            lastNonCachedSearch === NonCachedSearchType.searchBySpecificParts)
        ) {
          return true
        }
        if (lastNonCachedSearch === NonCachedSearchType.searchByText) {
          return true
        }
        return false
      }

      const filtersForOrder = availableFilters.filter(
        (filter) => filter.groupName !== FilterGroupName.STOCK
      )

      return buildRenderableFilterSections({
        filters: filtersForOrder,
        hasGfx: Boolean(lookupTitle),
        searchType: lastNonCachedSearch,
      }).filter((entry) => {
        if (entry.groupName === 'GFX') {
          return true
        }
        if (!entry.filter) {
          return false
        }
        if (shouldHideLinesFilter(entry.filter)) {
          return false
        }
        return true
      })
    }, [
      availableFilters,
      coverageKey,
      currentVehicle?.engine,
      lastNonCachedSearch,
      lookupTitle,
    ])

    const getAppliedCountForEntry = (sectionId: FilterSectionId): number =>
      getSectionAppliedFilterCount(
        sectionId,
        activeFilters,
        activeManufacturers
      )

    const renderFilterSection = (
      entry: (typeof renderableFilters)[number],
      suppressTopBorder = false
    ): ReactElement | null => {
      const accordionVariant = isDrawer ? 'drawer' : 'sidebar'
      const defaultExpanded = getFilterSectionDefaultExpanded(
        entry.groupName,
        accordionVariant
      )

      if (entry.groupName === 'GFX') {
        return (
          <FilterAccordionSection
            sectionKey="gfx-filter"
            title={getFilterGroupTitle('GFX')}
            appliedCount={getAppliedCountForEntry(entry.sectionId)}
            defaultExpanded={defaultExpanded}
            suppressTopBorder={suppressTopBorder}
            variant={accordionVariant}
          >
            <GfxFilter>
              <GfxFilterTitle>{lookupTitle}</GfxFilterTitle>
              <CloseIcon
                onClick={async () => {
                  await forceClearGfx()
                }}
                style={{ color: '#53A63A', cursor: 'pointer' }}
              />
            </GfxFilter>
          </FilterAccordionSection>
        )
      }

      const filter = entry.filter
      if (!filter) {
        return null
      }

      const groupNameId = filter.groupName?.toLowerCase().replace(' ', '-')
      const options: ListItem[] = filter.items.map((item) => ({
        value: item,
        label: item,
        active: isItemActive(filter, item),
        disabled: isItemDisabled(filter, item),
      }))

      if (
        showAllLinesForOrangeParts &&
        filter.groupName === FilterGroupName.LINES
      ) {
        options[2].active = true
      }

      if (filter.groupName === FilterGroupName.LINES) {
        return (
          <FilterAccordionSection
            sectionKey={`${groupNameId}-${filter.items.join('-')}`}
            title={getFilterGroupTitle(filter.groupName)}
            appliedCount={getAppliedCountForEntry(entry.sectionId)}
            defaultExpanded={defaultExpanded}
            suppressTopBorder={suppressTopBorder}
            variant={accordionVariant}
          >
            <Options>
              <SelectWrapper
                options={options}
                onSelect={(data) => {
                  const item = data[0]

                  if (item.value === LineTypes.ALL_LINES) {
                    setShowFilterLinesDrawer(true)
                    setShowSelectionList(true)
                  } else {
                    if (isDrawer) {
                      onStagedManufacturersChange?.([])
                    } else {
                      setSelectedLinesManufacturers([])
                    }
                    setShowSelectionList(false)
                  }

                  if (activeBoolean) {
                    setActiveBoolean(false)
                  }

                  handleOnFiltersChange(filter.groupName, data, true)
                }}
              />
              {showSelectionsList && activeManufacturers.length > 0 && (
                <SelectedLines resetSelection={handleResetSelection} />
              )}
            </Options>
          </FilterAccordionSection>
        )
      }

      return (
        <Fragment key={`${groupNameId}-${filter.items.join('-')}`}>
          <FilterAccordionSection
            sectionKey={`${groupNameId}-${filter.items.join('-')}`}
            title={getFilterGroupTitle(filter.groupName)}
            appliedCount={getAppliedCountForEntry(entry.sectionId)}
            defaultExpanded={defaultExpanded}
            suppressTopBorder={suppressTopBorder}
            variant={accordionVariant}
          >
            <Options>
              <CheckboxFilterGroup
                groupName={filter.groupName}
                options={options}
                onChange={handleOnFiltersChange}
              />
            </Options>
          </FilterAccordionSection>
          {filter.groupName === FilterGroupName.BRANDS && !isDrawer && (
            <Divider />
          )}
        </Fragment>
      )
    }

    return (
      <Wrapper $isDrawer={isDrawer}>
        <FiltersWrapper
          $isDrawer={isDrawer}
          filtersCollapsed={filtersCollapsed}
        >
          {filtersCollapsed && handleOnClose && (
            <DrawerHeader>
              <StyledCloseIcon onClick={handleOnClose} />
            </DrawerHeader>
          )}
          {showStockFilter && (
            <StockArea>
              <StockFilterSection
                hideLabel
                options={stockFilterOptions}
                selectedValue={selectedStockValue}
                onSelect={handleStockFilterSelect}
              />
            </StockArea>
          )}
          <FiltersList $isDrawer={isDrawer}>
            {renderableFilters.map((entry, index) => (
              <Fragment
                key={
                  entry.groupName === 'GFX'
                    ? 'gfx-filter'
                    : `${entry.groupName}-${entry.filter?.items.join('-') ?? ''}`
                }
              >
                {renderFilterSection(entry, showStockFilter && index === 0)}
              </Fragment>
            ))}
          </FiltersList>
        </FiltersWrapper>
        {partsBinId && <SelectLinesDrawer />}
      </Wrapper>
    )
  }
)

FilterResultsMenu.displayName = 'FilterResultsMenu'

const StyledCloseIcon = styled(CloseIcon)`
  cursor: pointer;
`
const Wrapper = styled.div<{ $isDrawer: boolean }>`
  margin-left: ${(p) => (p.$isDrawer ? '0' : '20px')};
  margin-right: ${(p) => (p.$isDrawer ? '0' : '10px')};
  font-family: Roboto, serif;
`

interface FiltersWrapperProps {
  filtersCollapsed: boolean
  $isDrawer: boolean
}

const FiltersWrapper = styled.div<FiltersWrapperProps>`
  padding: ${(p) => {
    if (p.$isDrawer) {
      return '0'
    }
    if (p.filtersCollapsed) {
      return '20px 0'
    }
    return '0 0 20px'
  }};
  background: ${defaultTheme.colors.white};
  width: ${(p) => (p.$isDrawer ? '100%' : '259px')};
  border-top: ${(p) =>
    p.$isDrawer ? 'none' : `4px solid ${defaultTheme.colors.black}`};
`

const DrawerHeader = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
  padding: 0 20px 8px;
`
const FiltersList = styled.div<{ $isDrawer: boolean }>`
  display: flex;
  flex-direction: column;
  text-align: left;
  min-width: 0;
  padding: 0 20px;

  ${(p) =>
    p.$isDrawer
      ? `
    overflow: hidden;
  `
      : ''}
`

const Options = styled.div`
  font-family: Roboto, serif;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 24px;
  color: ${defaultTheme.colors.text};
  mix-blend-mode: normal;
  opacity: 0.9;
  min-width: 0;
  width: 100%;
`

const GfxFilter = styled.div`
  min-width: 88px;
  height: 32px;
  background-color: ${defaultTheme.colors.gfxBackground};
  margin-top: 10px;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
`
const GfxFilterTitle = styled.span`
  font-family: Roboto, serif;
  font-style: normal;
  font-weight: bold;
  font-size: 18px;
  line-height: 24px;
  margin-right: 13px;
  color: ${defaultTheme.colors.gfxFilterTitle};
`

const Divider = styled.div`
  border-bottom: 1px solid ${defaultTheme.colors.border};
  width: 100%;
`

const StockArea = styled.div`
  background: #fafafa;
  padding: 16px 20px;
  border-bottom: 1px solid ${defaultTheme.colors.border};
  box-sizing: border-box;
  width: 100%;
`
